import React, { useRef, useState, useMemo } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { LifecycleStaleTicket } from './types';
import { formatDateWithTimezone } from '../../TicketingPortal/util/dateFormatter';
import SkeletonBlock from './SkeletonBlock';

interface StaleTicketsSectionProps {
  title: string;
  description: string;
  tickets: LifecycleStaleTicket[];
  totalCount: number;
  hasNextPage: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;

  /**
   * When true the table is replaced by a shimmering placeholder that mirrors
   * the real column layout, so the card keeps its size while data loads.
   */
  loading?: boolean;

  /** Number of placeholder rows rendered while loading. */
  skeletonRowCount?: number;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

/** Column widths shared by the real table and its skeleton. */
const SKELETON_COLUMNS: Array<{ style: object; kind: 'text' | 'badge'; width: number }> = [
  { style: { width: 150 }, kind: 'text', width: 90 },
  { style: { width: 300 }, kind: 'text', width: 240 },
  { style: { width: 155 }, kind: 'text', width: 110 },
  { style: { width: 180 }, kind: 'text', width: 130 },
  { style: { width: 130 }, kind: 'badge', width: 70 },
  { style: { width: 190 }, kind: 'badge', width: 110 },
  { style: { width: 110 }, kind: 'text', width: 40 },
  { style: { width: 150 }, kind: 'text', width: 100 },
  { style: { width: 220 }, kind: 'text', width: 150 },
];

const SKELETON_HEADERS = [
  'Ticket Number',
  'Ticket Title',
  'Category',
  'Sub-Category',
  'Urgency',
  'Current Status',
  'Age',
  'Submitted',
  'Assigned To',
];

/**
 * Placeholder table shown while stale tickets load.
 *
 * Kept in this file so any consumer gets the skeleton simply by passing
 * `loading`, and so it can reuse the exact row / column-width styles below.
 */
const StaleTicketsSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <View style={styles.card}>
    <View style={styles.cardHead}>
      <SkeletonBlock style={styles.skeletonTitle} />
      <View style={styles.headerActions}>
        <SkeletonBlock style={styles.skeletonNote} />
        <SkeletonBlock style={styles.skeletonSearch} />
      </View>
    </View>

    <ScrollView horizontal showsHorizontalScrollIndicator={true} contentContainerStyle={styles.scrollContent}>
      <View style={styles.tableWrap}>
        {/* Real header row keeps the column geometry identical to the loaded table. */}
        <View style={[styles.row, styles.headerRow]}>
          {SKELETON_HEADERS.map((label, index) => (
            <View key={label} style={[styles.headerCellWrap, SKELETON_COLUMNS[index].style]}>
              <Text style={styles.headerCell}>{label}</Text>
            </View>
          ))}
        </View>

        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <View style={styles.row} key={`stale-skeleton-row-${rowIndex}`}>
            {SKELETON_COLUMNS.map((column, columnIndex) => (
              <View
                key={`stale-skeleton-cell-${rowIndex}-${columnIndex}`}
                style={[styles.skeletonCell, column.style]}
              >
                <SkeletonBlock
                  style={
                    column.kind === 'badge'
                      ? [styles.skeletonBadge, { width: column.width }]
                      : [styles.skeletonText, { width: column.width }]
                  }
                />
              </View>
            ))}
          </View>
        ))}
      </View>
    </ScrollView>

    <View style={styles.footerInfo}>
      <SkeletonBlock style={styles.skeletonFooter} />
    </View>
  </View>
);

const URGENCY_BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  Critical: { bg: '#FFE5DE', color: '#B52C07' },
  High: { bg: '#F9E0CA', color: '#8A4A04' },
  Medium: { bg: '#DAEEFA', color: '#01346F' },
  Low: { bg: '#AAD2E2', color: '#1E5A7A' },
};

const STATUS_BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  New: { bg: '#DAEEFA', color: '#01346F' },
  'In Progress': { bg: '#FEF1B9', color: '#91550A' },
  'Escalated to IT': { bg: '#E1E3FD', color: '#31217D' },
  'Escalated to TMV': { bg: '#AAD2E2', color: '#1E5A7A' },
  'Submitted to ADO': { bg: '#EBEBEB', color: '#4B4B4B' },
  'Waiting on User': { bg: '#FCECDE', color: '#4D2E0C' },
};

const renderBadge = (label: string, map: Record<string, { bg: string; color: string }>) => {
  const style = map[label] ?? { bg: '#EBEBEB', color: '#4B4B4B' };
  return (
    <View style={[styles.badge, { backgroundColor: style.bg }]}> 
      <Text style={[styles.badgeText, { color: style.color }]}>{label}</Text>
    </View>
  );
};

const StaleTicketsSection: React.FC<StaleTicketsSectionProps> = ({
  title,
  description,
  tickets,
  totalCount,
  hasNextPage,
  onLoadMore,
  isLoadingMore = false,
  loading = false,
  skeletonRowCount = 8,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const isLoadingMoreRef = useRef(false);
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState<
    | 'ticketNumber'
    | 'ticketTitle'
    | 'categoryName'
    | 'subCategoryName'
    | 'urgency'
    | 'currentStatus'
    | 'age'
    | 'submitted'
    | 'assignedTo'
  >('ticketNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!onLoadMore || !hasNextPage || isLoadingMore || isLoadingMoreRef.current) return;
    
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    const distanceFromEnd = contentSize.width - (contentOffset.x + layoutMeasurement.width);
    
    // Trigger load more when within 500px of the right edge
    if (distanceFromEnd < 500) {
      isLoadingMoreRef.current = true;
      onLoadMore();
    }
  };

  React.useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    const defaultAsc = ['ticketNumber', 'ticketTitle', 'categoryName', 'subCategoryName', 'urgency', 'currentStatus', 'assignedTo'].includes(key);
    setSortKey(key);
    setSortDirection(defaultAsc ? 'asc' : 'desc');
  };

  const filteredTickets = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((item) => {
      return (
        String(item.ticketNumber).toLowerCase().includes(q) ||
        String(item.ticketTitle || '').toLowerCase().includes(q) ||
        String(item.categoryName || '').toLowerCase().includes(q) ||
        String(item.subCategoryName || '').toLowerCase().includes(q) ||
        String(item.assignedTo || '').toLowerCase().includes(q)
      );
    });
  }, [tickets, searchText]);

  const sortedTickets = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    const getComparable = (item: any): string | number => {
      if (sortKey === 'age') return Number(item.age) || 0;
      if (sortKey === 'submitted') return new Date(item.submitted).getTime() || 0;
      return String(item[sortKey] ?? '').toLowerCase();
    };

    return [...filteredTickets].sort((a, b) => {
      const left = getComparable(a);
      const right = getComparable(b);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * dir;
      }

      return String(left).localeCompare(String(right)) * dir;
    });
  }, [filteredTickets, sortKey, sortDirection]);

  if (loading) {
    return <StaleTicketsSkeleton rowCount={skeletonRowCount} />;
  }

  if (error) {
    return (
      <View style={styles.card}>
        <View style={styles.cardHead}>
          <Text style={styles.cardTitle}>{title}</Text>
          <View style={styles.headerActions}>
            <Text style={styles.cardNote}>{description}</Text>
          </View>
        </View>
        <View style={styles.errorWrap}>
          <Text style={styles.errorText}>{error}</Text>
          {!!onRetry && (
            <TouchableOpacity onPress={onRetry} style={styles.retryButton} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryText}>↻  Retry</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardTitle}>{title}</Text>
        <View style={styles.headerActions}>
          <Text style={styles.cardNote}>{description}</Text>
          <View style={styles.searchWrap}>
            <Svg width={15} height={15} viewBox="0 0 24 24" style={styles.searchIcon}>
              <Path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#818181" />
            </Svg>
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder="Search tickets"
              placeholderTextColor="#8A8E94"
              style={styles.searchInput}
            />
          </View>
        </View>
      </View>

      <ScrollView 
        ref={scrollViewRef}
        horizontal 
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={styles.scrollContent}
        onScroll={handleScroll}
        scrollEventThrottle={200}
      >
        <View style={styles.tableWrap}>
          <View style={[styles.row, styles.headerRow]}>
            <TouchableOpacity onPress={() => handleSort('ticketNumber')} style={[styles.headerCellWrap, styles.ticketNo]}> 
              <Text style={[styles.headerCell]}>Ticket Number{sortKey === 'ticketNumber' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('ticketTitle')} style={[styles.headerCellWrap, styles.ticketTitle]}> 
              <Text style={[styles.headerCell]}>Ticket Title{sortKey === 'ticketTitle' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('categoryName')} style={[styles.headerCellWrap, styles.category]}> 
              <Text style={[styles.headerCell]}>Category{sortKey === 'categoryName' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('subCategoryName')} style={[styles.headerCellWrap, styles.subCategory]}> 
              <Text style={[styles.headerCell]}>Sub-Category{sortKey === 'subCategoryName' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('urgency')} style={[styles.headerCellWrap, styles.urgency]}> 
              <Text style={[styles.headerCell]}>Urgency{sortKey === 'urgency' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('currentStatus')} style={[styles.headerCellWrap, styles.status]}> 
              <Text style={[styles.headerCell]}>Current Status{sortKey === 'currentStatus' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('age')} style={[styles.headerCellWrap, styles.age]}> 
              <Text style={[styles.headerCell, styles.numericHeader]}>Age{sortKey === 'age' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('submitted')} style={[styles.headerCellWrap, styles.submitted]}> 
              <Text style={[styles.headerCell, styles.numericHeader]}>Submitted{sortKey === 'submitted' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleSort('assignedTo')} style={[styles.headerCellWrap, styles.assigned]}> 
              <Text style={[styles.headerCell]}>Assigned To{sortKey === 'assignedTo' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.bodyScroll} nestedScrollEnabled>
            <View style={styles.tableBody}>
              {sortedTickets.length ? (
                sortedTickets.map((item) => (
                  <View style={styles.row} key={item.ticketNumber}>
                    <Text style={[styles.cell, styles.ticketNo, styles.link]}>{item.ticketNumber}</Text>
                    <Text style={[styles.cell, styles.ticketTitle]}>{item.ticketTitle}</Text>
                    <Text style={[styles.cell, styles.category]}>{item.categoryName}</Text>
                    <Text style={[styles.cell, styles.subCategory]}>{item.subCategoryName}</Text>
                    <View style={[styles.cell, styles.urgency, styles.badgeCell]}>
                      {renderBadge(item.urgency, URGENCY_BADGE_STYLES)}
                    </View>
                    <View style={[styles.cell, styles.status, styles.badgeCell]}>
                      {renderBadge(item.currentStatus, STATUS_BADGE_STYLES)}
                    </View>
                    <Text style={[styles.cell, styles.age, styles.numericCell, styles.ageText]}>{item.age}</Text>
                    <Text style={[styles.cell, styles.submitted, styles.numericCell]}>{formatDateWithTimezone(item.submitted)}</Text>
                    <Text style={[styles.cell, styles.assigned]}>{item.assignedTo}</Text>
                  </View>
                ))
              ) : (
                <View style={styles.emptyWrap}>
                  <Text style={styles.noResults}>{searchText ? `No results found for "${searchText}"` : 'Try expanding your date range or removing some filters.'}</Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </ScrollView>

      {/* Loading More Indicator */}
      {isLoadingMore && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#005CBC" />
          <Text style={styles.loadingText}>Loading more tickets...</Text>
        </View>
      )}

      {/* Footer Info */}
      <View style={styles.footerInfo}>
        <Text style={styles.footerText}>
          Showing {Math.min(sortedTickets.length, totalCount)} of {totalCount} tickets
          {hasNextPage && ' • Scroll to load more'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    marginHorizontal: 0,
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHead: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  cardNote: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#7A8088',
  },
  scrollContent: {
    flexGrow: 1,
  },
  tableWrap: {
    minWidth: 1400,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  tableBody: {
    width: '100%',
  },
  bodyScroll: {
    maxHeight: 496,
    paddingHorizontal: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#D7E5F8',
    minHeight: 56,
    backgroundColor: '#FFFFFF',
  },
  headerRow: {
    borderBottomColor: '#D7E5F8',
    borderBottomWidth: 2,
    minHeight: 48,
    backgroundColor: '#F9FAFB',
  },
  headerCellWrap: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cell: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 12,
    paddingHorizontal: 16,
    textAlignVertical: 'center',
  },
  headerCell: {
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  errorWrap: {
    minHeight: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  numericHeader: {
    textAlign: 'right',
  },
  numericCell: {
    textAlign: 'right',
  },
  ticketNo: { width: 150 },
  ticketTitle: { width: 300 },
  category: { width: 155 },
  subCategory: { width: 180 },
  urgency: { width: 130 },
  status: { width: 190 },
  age: { width: 110 },
  submitted: { width: 150 },
  assigned: { width: 220 },
  link: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  badgeCell: {
    justifyContent: 'center',
  },
  badge: {
    alignSelf: 'flex-start',
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: FontFamily.medium,
  },
  ageText: {
    color: '#C12C01',
    fontFamily: FontFamily.bold,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    gap: 8,
    backgroundColor: '#F9FAFB',
    borderTopWidth: 1,
    borderTopColor: '#E8EDF3',
  },
  loadingText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  footerInfo: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#FAFBFC',
    borderTopWidth: 1,
    borderTopColor: '#E8EDF3',
  },
  footerText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E8EDF3',
    backgroundColor: '#FAFBFC',
  },
  paginationInfo: {
    flex: 1,
  },
  paginationText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  paginationControls: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  paginationButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    minWidth: 90,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationButtonDisabled: {
    backgroundColor: '#F3F4F6',
    borderColor: '#E5E7EB',
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#374151',
  },
  paginationButtonTextDisabled: {
    color: '#9CA3AF',
  },
  pageSizeContainer: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
  },
  pageSizeLabel: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
    marginRight: 4,
  },
  pageSizeButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 36,
  },
  pageSizeButtonActive: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  pageSizeButtonText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
  },
  pageSizeButtonTextActive: {
    color: '#FFFFFF',
  },
  searchWrap: {
    width: 260,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 32,
    backgroundColor: '#FFFFFF',
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 0,
    flex: 1,
  },

  emptyWrap: { minHeight: 160, justifyContent: 'center', alignItems: 'center' },
  noResults: { fontSize: 14, color: '#7A8088', fontFamily: FontFamily.medium, textAlign: 'center' },

  // ─── Skeleton ─────────────────────────────────────────────────────────────
  skeletonTitle: {
    width: 150,
    height: 14,
  },
  skeletonNote: {
    width: 260,
    height: 10,
  },
  skeletonSearch: {
    width: 260,
    height: 32,
    borderRadius: 6,
  },
  skeletonCell: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  skeletonText: {
    height: 10,
  },
  skeletonBadge: {
    height: 24,
    borderRadius: 4,
  },
  skeletonFooter: {
    width: 180,
    height: 10,
  },
});

export default StaleTicketsSection;






